import React, { Fragment, h } from "preact";
import { AppPage } from "../_common/components/app-page";
import { Card, CF, R } from "../_common/layout";
import { t } from "../_i18n";
import { useHistory } from "react-router";
import { connect } from "react-redux";
import { useEffect, useState, useMemo, useRef } from "preact/hooks";
import { Form as FinalForm } from "react-final-form";
import {
  Select,
  UploadFile,
  FormFieldSplittedDate,
} from "../_common/forms/components/components";
import ButtonSpinner from "../_common/components/buttons/buttonSpinner/buttonSpinner";
import Badge from "../_common/components/Badge";
import Spinner from "../_common/components/Spinner";
import {
  getCertApplicationStatus,
  getAceCertApplicationStatus,
  uploadCertPaymentReceipt,
  uploadAceCertPaymentReceipt,
  generateVoucher,
} from "../_rx/certificate-application/actions";

const STATUS_PENDING_APPLICATION = 1;
const STATUS_VALIDATED = 2;
const STATUS_UPLOADED_GITU = 3;
const STATUS_VALIDATED_INSPECTION = 4;
const STATUS_REJECTED = 5;

const statusBadgeTextDic = {
  [STATUS_PENDING_APPLICATION]: t("Pendiente validación"),
  [STATUS_UPLOADED_GITU]: t("Validado"),
  [STATUS_VALIDATED_INSPECTION]: t("Validado"),
  [STATUS_VALIDATED]: t("Validado"),
  [STATUS_REJECTED]: t("Rechazado"),
};

const statusBadgeClassDic = {
  [STATUS_PENDING_APPLICATION]: "badge-warning",
  [STATUS_UPLOADED_GITU]: "badge-success translucent",
  [STATUS_VALIDATED_INSPECTION]: "badge-success translucent",
  [STATUS_VALIDATED]: "badge-success translucent",
  [STATUS_REJECTED]: "badge-danger",
};

const bonosOptionsList = [
  {
    name: "Gratis",
    key: "free",
  },
  {
    name: "Bono 99€",
    key: "general_voucher",
  },
  {
    name: "Bono nuevo alumno 99€",
    key: "new_student_voucher",
  },
];

const BadgeComponent = ({ status, extraClass }) => (
  <Badge
    className={`t-upper width-fit-content badge font-small ${extraClass || ""} ${statusBadgeClassDic[status] || "d-none"}`}
  >
    {t(statusBadgeTextDic[status])}
  </Badge>
);

function CertificateApplication({
  studentId,
  finishedAcademicPrograms,
  getCertApplicationStatus,
  getAceCertApplicationStatus,
  certificateApplicationStatus,
  aceCertificateApplicationStatus,
  uploadCertPaymentReceipt,
  uploadAceCertPaymentReceipt,
  generateVoucher,
  aportaEmpresa,
  bonoGenerado
}) {
  /** Variables **/
  const formRef = useRef(null);
  const academicPrograms = finishedAcademicPrograms
    ?.filter(
      (ap) =>
        (ap.formative_zone === "MAD" || ap.formative_zone === "CAT") && ap.closed_academic_record,
    )
    .map((ap) => ({
      name: ap.title + " " + `(${ap.formative_zone_code})`,
      key: ap.id,
    }));
  const showMultipleCicles = academicPrograms.length > 1;

  /** States **/
  const [file, setFile] = useState(null);
  const [aceFile, setAceFile] = useState(null);
  const [fileURL, setFileURL] = useState("#");
  const [aceFileURL, setAceFileURL] = useState("#");
  const [loading, setLoading] = useState(false);
  const [academicProgramId, setAcademicProgramId] = useState(
    academicPrograms[0].key,
  );
  const [bonoId, setBonoId] = useState(bonosOptionsList[0].key);
  const [isLoading, setIsLoading] = useState(true);
  const [bonoEnviado, setBonoEnviado] = useState(false);

  /** Memos **/
  const selectedAP = useMemo(
    () => finishedAcademicPrograms.find((ap) => ap.id === academicProgramId),
    [academicProgramId, finishedAcademicPrograms],
  );
  const isMad = selectedAP?.formative_zone === "MAD";
  const isCat = selectedAP?.formative_zone === "CAT";

  /** Lifecycles **/
  useEffect(() => {
    const fetchData = async () => {
      await getCertApplicationStatus(studentId, academicProgramId);
      await getAceCertApplicationStatus(studentId, academicProgramId);
      setIsLoading(false);
    };

    fetchData();
  }, [studentId]);

  /** Methods **/
  const shouldShowForm = (status) => {
    if (!status?.documents) return true;
    switch (status?.status_id) {
      case STATUS_REJECTED:
      case undefined:
        return true;
      default:
        return false;
    }
  };

  const shouldShowRequestCard = (status) => {
    if (!status?.documents) return false;
    return true;
  };

  const formatDateDDMMYYYY = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const replaceCertDate = (html, date) => {
    if (!html || !date) return html;
    return html.replace("dd-mm-aaaa", formatDateDDMMYYYY(date));
  };

  const handleUploadFile = (event, type) => {
    let eventFile = event.target.files[0];
    if (type === "ace") {
      setAceFile(eventFile);
      setAceFileURL(URL.createObjectURL(eventFile));
    } else {
      setFile(eventFile);
      setFileURL(URL.createObjectURL(eventFile));
    }
  };

  const handleAPSelect = async (e) => {
    const academicProgramId = +e.target.value;

    setAcademicProgramId(academicProgramId);

    formRef.current?.reset();
    handleRemoveFiles();

    setIsLoading(true);
    await getCertApplicationStatus(studentId, academicProgramId);
    await getAceCertApplicationStatus(studentId, academicProgramId);
    setIsLoading(false);
  };

  const handleRemoveFiles = () => {
    setFile(null);
    setFileURL("#");
    setAceFile(null);
    setAceFileURL("#");
  };

  const handleSubmitFile = async (data) => {
    setLoading(true);
    if (file) {
      setLoading(true);
      await uploadCertPaymentReceipt(
        studentId,
        file,
        academicProgramId,
        data.payed_at,
      );
      setLoading(false);
    }
    if (aceFile) {
      setLoading(true);
      await uploadAceCertPaymentReceipt(studentId, {
        file: aceFile,
        academicProgramId,
      });
      setLoading(false);
    }
    setLoading(true);
    await getCertApplicationStatus(studentId, academicProgramId);
    await getAceCertApplicationStatus(studentId, academicProgramId);
    setLoading(false);
    handleRemoveFiles();
    setLoading(false);
  };

  const getCertificateApplicationHtml = () => {
    const statusId =
      certificateApplicationStatus?.documents?.length &&
      certificateApplicationStatus?.status_id;
    const aceStatusId =
      aceCertificateApplicationStatus?.documents?.length &&
      aceCertificateApplicationStatus?.status_id;
    const isPending = statusId === STATUS_PENDING_APPLICATION;
    const isRejected = statusId === STATUS_REJECTED;
    const aceIsRejected = aceStatusId === STATUS_REJECTED;
    const isValidated =
      statusId === STATUS_VALIDATED ||
      statusId === STATUS_UPLOADED_GITU ||
      statusId === STATUS_VALIDATED_INSPECTION;

    if (isRejected || aceIsRejected) {
      const html = t("cert_app_not_validated");

      const comments = [
        certificateApplicationStatus?.comments,
        aceCertificateApplicationStatus?.comments,
      ].filter(Boolean);

      const error = comments.length
        ? `<ul>${comments.map(c => `<li>- ${c}</li>`).join('')}</ul>`
        : t("Documento invalido.");

      return replaceErrorMessage(html, error);
    }

    if (isPending) return t("cert_app_pending");

    if (isValidated) {
      const html = t("cert_app_validated");
      const date = certificateApplicationStatus?.created_at;
      return replaceCertDate(html, date).replace('{{TASAS_INTERNAS}}', aportaEmpresa ? 'También' : 'Cuando hayas pagado las tasas internas de iLERNA, también');
    }

    if (isCat) {
      return t("cat_cert_application");
    }

    if (isMad) {
      return t("mad_cert_application");
    }

    return null;
  };

  const replaceErrorMessage = (html, error) => {
    if (!html || !error) return html;
    return html.replace("{{$ERROR_MESSAGE}}", error);
  };

  const isCertificateValidated = (status) => {
    if (!status?.documents) return false;
    switch (status?.status_id) {
      case STATUS_VALIDATED:
      case STATUS_UPLOADED_GITU:
      case STATUS_VALIDATED_INSPECTION:
        return true;
      default:
        return false;
    }
  };

  const handleBonoSubmit = async () => {
    setLoading(true);
    const resp = await generateVoucher(
      selectedAP?.formative_zone,
      studentId,
      {
        academicProgramId: academicProgramId,
        voucherType: bonoId
      })

    if(!resp.error) {
      setBonoEnviado(true);
    }

    setLoading(false);
  };

  const renderRequestCard = (request) => {
    return (
      <Card smOnly={false}>
        <p className="mb-1 font-bold">{request.academic_program_name}</p>
        <BadgeComponent status={request?.status_id} />
        {request.comments && (
          <Fragment>
            <p className={"font-color-soft-gray mt-2"}>{t("Comentarios")}</p>
            <p>{request.comments}</p>
          </Fragment>
        )}
      </Card>
    );
  };

  const renderSpinner = () => (
    <div className="flex justify-center my-6">
      <div className="relative h-6">
        <Spinner className="w-6 h-6" />
      </div>
      <p className="pl-10 pt-0 text-center">Cargando datos ...</p>
    </div>
  );

  /** Render **/
  return (
    <CF className="max-width-980 m-x2-top">
      {showMultipleCicles && (
        <div className="mb-4">
          <div className="font-bold">{t("Selecciona el ciclo")}:</div>
          <Select
            noEmptyOption
            options={academicPrograms}
            value={academicProgramId}
            onChange={handleAPSelect}
            required
          />
        </div>
      )}
      <FinalForm
        onSubmit={handleSubmitFile}
        initialValues={{ payed_at: null }}
        validate={(values) => {
          const errors = {};
          if (!values.payed_at) {
            errors.payed_at = t("Campo obligatorio");
          }
          return errors;
        }}
        render={({ handleSubmit, form }) => {
          formRef.current = form;
          const { invalid, submitting } = form.getState();
          return (
            <Fragment>
              {isLoading ? (
                renderSpinner()
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: getCertificateApplicationHtml(),
                  }}
                />
              )}
              {!isLoading && (
                <Fragment>
                  {aportaEmpresa && !bonoGenerado && !bonoEnviado &&
                    isCertificateValidated(certificateApplicationStatus) && (
                      <Fragment>
                        <div className="mb-2 mt-4 font-bold">
                          {t("Selecciona el tipo de bono")}:
                        </div>
                        <div className="flex gap-4">
                          <div className="w-9/12">
                            <Select
                              noEmptyOption
                              options={bonosOptionsList}
                              value={bonoId}
                              onChange={(event) =>
                                setBonoId(event.target.value)
                              }
                              required
                            />
                          </div>
                          <div className="w-3/12">
                            <ButtonSpinner
                              showSpinner={loading}
                              disabled={submitting}
                              className="h-[48px]"
                              onClick={() => handleBonoSubmit()}
                            >
                              {t("Seleccionar bono")}
                            </ButtonSpinner>
                          </div>
                        </div>
                      </Fragment>
                    )}
                  <div className="my-4 flex gap-4">
                    {!isCertificateValidated(certificateApplicationStatus) && (
                      <div className="w-full bg-white p-4">
                        <h2 className="font-bold">
                          {t("Comprobante del pago de tasas en educación")}
                        </h2>
                        {shouldShowForm(certificateApplicationStatus) ? (
                          <div>
                            {!file ? (
                              <div className="pt-6">
                                <UploadFile
                                  classNameButton="btn btn-ghost"
                                  accept="application/pdf,.docx,.doc,.odt"
                                  onChange={handleUploadFile}
                                >
                                  {t("Subir archivo")}
                                </UploadFile>
                                <span className="font-color-879CA1">
                                  {t("transfer_expedient_doc_types")}
                                </span>
                              </div>
                            ) : (
                              <div className="m-bottom d-flex flex-column sm-no-flex-column mt-2">
                                <a
                                  className="m-r font-color-149BAB m-bottom sm-no-m-bottom"
                                  href={fileURL}
                                  download={file.name}
                                >
                                  {file.name}
                                </a>
                                <span
                                  className="font-color-soft-gray t-underline cursor-pointer"
                                  onClick={handleRemoveFiles}
                                >
                                  {t("Quitar archivo")}
                                </span>
                              </div>
                            )}
                            <form
                              id="payed-form"
                              onSubmit={handleSubmit}
                              noValidate
                            >
                              <h2 className="-mb-2 mt-4 font-bold">
                                {t("Fecha en la que se ha pagado la tasa:")}
                              </h2>
                              <R>
                                <div className="col-100 md-col-60">
                                  <FormFieldSplittedDate
                                    key={academicProgramId}
                                    name="payed_at"
                                  />
                                </div>
                              </R>
                              <button type="submit" hidden />
                            </form>
                          </div>
                        ) : null}
                        {shouldShowRequestCard(certificateApplicationStatus) && (
                          <Fragment>
                            <hr className="mb-3 mt-2" />
                            <h2 className="m-bottom font-bold">
                              {t("Estado de la petición")}
                            </h2>
                          </Fragment>
                        )}

                        {shouldShowRequestCard(certificateApplicationStatus) &&
                          renderRequestCard(certificateApplicationStatus)}
                      </div>
                    )}
                    {!isCertificateValidated(aceCertificateApplicationStatus) && (
                      <div className="w-full bg-white p-4">
                        <div>
                          <h2 className="font-bold">
                            {t(
                              "Justificación que acredite la reducción o exención de tasas (opcional)",
                            )}
                          </h2>
                          {shouldShowForm(aceCertificateApplicationStatus) ? (
                            <div>
                              {!aceFile ? (
                                <div className="pt-6">
                                  <UploadFile
                                    classNameButton="btn btn-ghost"
                                    accept="application/pdf,.docx,.doc,.odt"
                                    onChange={(e) => handleUploadFile(e, "ace")}
                                  >
                                    {t("Subir archivo")}
                                  </UploadFile>
                                  <span className="font-color-879CA1">
                                    {t("transfer_expedient_doc_types")}
                                  </span>
                                </div>
                              ) : (
                                <div className="m-bottom d-flex flex-column sm-no-flex-column mt-2">
                                  <a
                                    className="m-r font-color-149BAB m-bottom sm-no-m-bottom"
                                    href={aceFileURL}
                                    download={aceFile.name}
                                  >
                                    {aceFile.name}
                                  </a>
                                  <span
                                    className="font-color-soft-gray t-underline cursor-pointer"
                                    onClick={handleRemoveFiles}
                                  >
                                    {t("Quitar archivo")}
                                  </span>
                                </div>
                              )}
                            </div>
                          ) : null}
                        </div>
                        {shouldShowRequestCard(aceCertificateApplicationStatus) && (
                          <Fragment>
                            <hr className="mb-3 mt-2" />
                            <h2 className="m-bottom font-bold">
                              {t("Estado de la petición")}
                            </h2>
                          </Fragment>
                        )}
                        {shouldShowRequestCard(aceCertificateApplicationStatus) &&
                          renderRequestCard(aceCertificateApplicationStatus)}
                      </div>
                    )}
                  </div>
                  {(shouldShowForm(certificateApplicationStatus) ||
                    shouldShowForm(aceCertificateApplicationStatus)) && (
                      <div className="flex w-full justify-end">
                        <ButtonSpinner
                          showSpinner={loading}
                          disabled={
                            !aceFile &&
                            (!file || !academicProgramId || invalid || submitting)
                          }
                          onClick={() =>
                            file
                              ? document
                                .getElementById("payed-form")
                                ?.requestSubmit()
                              : handleSubmitFile()
                          }
                        >
                          {t("Enviar solicitud")}
                        </ButtonSpinner>
                      </div>
                    )}
                </Fragment>
              )}
            </Fragment>
          );
        }}
      />
    </CF>
  );
}

CertificateApplication = connect(
  ({ auth, certificateApplication, status }) => {
    const academicProgram = status.student_meta.finished_academic_programs
      ?.find(enrolledAcademicProgram => enrolledAcademicProgram.id === status.student_meta.academic_program_id);
    
    return {
      studentId: auth.loggedUser.id,
      certificateApplicationStatus: certificateApplication?.status,
      aceCertificateApplicationStatus: certificateApplication?.aceStatus,
      finishedAcademicPrograms: status.student_meta.finished_academic_programs,
      aportaEmpresa: academicProgram?.company_provided,
      bonoGenerado: academicProgram?.voucher_generated,
      academicProgram: academicProgram,
    };
  },
  {
    getCertApplicationStatus,
    getAceCertApplicationStatus,
    uploadCertPaymentReceipt,
    uploadAceCertPaymentReceipt,
    generateVoucher,
  },
)(CertificateApplication);

export default () => {
  const history = useHistory();
  return (
    <AppPage
      name="certificate-application"
      title={t("Solicitud de título")}
      back={history.goBack}
      class="max-width-980"
    >
      <CertificateApplication />
    </AppPage>
  );
};
